import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { paths } from "config/paths";
import { useAudio } from "hooks/use-audio";
import { useSystemAudioContext } from "hooks/system-audio-context";
import { NewCall, type CallMode } from "screens/new-call";
import endCallIcon from "assets/end-call.svg";
import { useCues } from "hooks/use-cues";
import { useCallAudio } from "hooks/use-call-audio";
import { useCallscriptProgress } from "hooks/use-callscript-progress";
import { usePictureInPicture } from "hooks/use-picture-in-picture";
import { useConfigure } from "hooks/use-configure";
import { useCallScriptGraph } from "hooks/use-callscript-graph";

import type { FlatNode } from "./callscript-graph-to-nested";
import { Cues } from "./cues";
import { CallData } from "./call-data";
import { ErrorOverlay } from "./error-overlay";
import { CallCompletionOverlay } from "./call-completion-overlay";
import styles from "./index.module.css";
import { Node } from "./node";
import { PipActiveOverlay } from "./pip-active-overlay";
import { CallProgress } from "./call-progress";
import { CallSkip } from "./call-skip";
import { NODE_TYPES } from "constants/nodes";
import { EmptyState } from "./empty-state";
import { ListenerMode } from "./listener-mode";

export const Call = () => {
  const navigate = useNavigate();

  const [isCallActive, setIsCallActive] = useState(false);
  const [showCompletionOverlay, setShowCompletionOverlay] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [autoFoundNodeId, setAutoFoundNodeId] = useState<number | null>(null);
  const [visitedGroups, setVisitedGroups] = useState(new Set<number>());
  const [mode, setMode] = useState<CallMode>("script");

  const { audioConfig: baseAudioConfig } = useAudio();
  const { stream: systemAudioStream } = useSystemAudioContext();
  const audioConfig = useMemo(
    () => ({ ...baseAudioConfig, systemAudioStream }),
    [baseAudioConfig, systemAudioStream],
  );

  const { isSupported, isActive: PipIsActive, open, close, PipPortal } = usePictureInPicture();

  const {
    isLoadingChannels,
    isLoadingOrganisations,
    activeChannelId,
    activeOrganisationId,
    setActiveChannelId,
    channels,
    fetchOrganisations,
    fetchChannels,
    organisations,
    setActiveOrganisationId,
  } = useConfigure();

  const handleChannelChange = (value: string | number) => {
    const channelId = Number(value);
    setActiveChannelId(channelId);
  };

  const handleOrganisationChange = (value: string | number) => {
    const orgId = Number(value);
    setActiveOrganisationId(orgId);
  };

  const {
    fetchCallScriptGraph,
    graph,
    structuredGraph,
    flattenedGraph,
    orderedGroups,
    noActiveScript,
    graphLoadFailed,
    isLoading: isLoadingGraph,
  } = useCallScriptGraph(activeOrganisationId, activeChannelId);

  const organisationId = Number(activeOrganisationId);
  const channelId = Number(activeChannelId);

  const { pendingCues, onAcknowledge, resetCues, triggered, onCue } = useCues();

  const {
    currentNodeId,
    completedNodeIds,
    onTranscript,
    resetProgress,
    setCurrentNode,
    markNodeCompleted,
    endScript,
  } = useCallscriptProgress(flattenedGraph, setAutoFoundNodeId, setVisitedGroups);

  const {
    isRecording,
    isConnected,
    connectionState,
    error: audioError,
    startRecording,
    stopRecording,
    webSocketIsReady,
    analyserNode,
  } = useCallAudio({
    organisationId,
    channelId,
    audioConfig,
    // In script mode only agent speech (channel 0) should drive callscript
    // progress. Customer speech on channel 1 must not advance the script.
    onTranscript:
      mode === "script"
        ? (t) => {
            if ((t.channel ?? 0) === 0) onTranscript(t);
          }
        : undefined,
    onCue,
    shouldConnect: isCallActive,
  });

  const isCallConnected = isConnected && connectionState === "Open";
  const currentNode = useMemo(
    () => flattenedGraph?.find((node) => node.id === currentNodeId),
    [flattenedGraph, currentNodeId],
  );

  useEffect(() => {
    fetchOrganisations();
  }, [fetchOrganisations]);

  useEffect(() => {
    if (activeOrganisationId) {
      fetchChannels(activeOrganisationId);
    }
  }, [activeOrganisationId, fetchChannels]);

  useEffect(() => {
    if (mode === "script" && activeOrganisationId && activeChannelId) {
      fetchCallScriptGraph();
    }
  }, [mode, activeChannelId, activeOrganisationId, fetchCallScriptGraph]);

  useEffect(() => {
    if (audioError) {
      toast.error(audioError);
    }
  }, [audioError]);

  useEffect(() => {
    if (!isCallActive && isRecording) {
      stopRecording();
    }
  }, [isCallActive, isRecording, stopRecording]);

  const pipOptions = useMemo(() => {
    const _dim =
      mode === "listener"
        ? { width: 420, height: 500 }
        : { width: 420, height: window.outerHeight };
    return _dim;
  }, [mode]);

  const startCall = useCallback(async () => {
    if (!isCallActive) {
      try {
        setIsCallActive(true);
        setVisitedGroups(new Set());
        if (!PipIsActive) {
          open(pipOptions);
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Failed to start call";
        toast.error(errorMessage);
      }
    }
  }, [PipIsActive, isCallActive, open, pipOptions]);

  const endCall = useCallback(() => {
    if (isCallActive) {
      stopRecording();
      setIsCallActive(false);
      setShowCompletionOverlay(true);
      setAutoFoundNodeId(null);
    }
  }, [isCallActive, stopRecording]);

  useEffect(() => {
    const load = async () => {
      if (webSocketIsReady && !isRecording) {
        await startRecording();
      }
    };
    load();
  }, [startRecording, webSocketIsReady, isRecording]);

  const finishScript = useCallback(
    (node: FlatNode) => {
      if (node.groupId) {
        setVisitedGroups((visited: Set<number>) => new Set([...visited, node.groupId!]));
      }
      endScript();
      setShowCompletionOverlay(true);
      setIsCallActive(false);
    },
    [endScript, setVisitedGroups],
  );

  const handleSelectOption = useCallback(
    (id: string) => {
      const selectedNodeId = Number(id);
      const selectedNode = flattenedGraph?.find((node) => node.id === selectedNodeId);

      if (!selectedNode) {
        return;
      }

      markNodeCompleted(selectedNodeId);
      setAutoFoundNodeId(null);

      if (selectedNode.childIds.length > 0) {
        setCurrentNode(selectedNode.childIds[0]);
      } else {
        finishScript(selectedNode);
      }
    },
    [setCurrentNode, markNodeCompleted, flattenedGraph, finishScript],
  );

  const handleChangeOption = useCallback(
    (id: string | number | null, proceed: boolean = true) => {
      const selectedNodeId = Number(id);
      const selectedNode = flattenedGraph?.find((node) => node.id === selectedNodeId);

      if (!selectedNode) {
        return;
      }

      setAutoFoundNodeId(null);

      if (proceed === true) {
        if (selectedNode.childIds.length > 0) {
          if (selectedNode?.type === NODE_TYPES.OPTION) {
            toast.warning("Please select an option");
          } else {
            setCurrentNode(selectedNode.childIds[0]);
          }
        } else {
          markNodeCompleted(selectedNodeId);
          finishScript(selectedNode);
        }
      } else {
        if (selectedNode.parentId) {
          const parentNodeId = selectedNode?.parentId;
          const parentNode = flattenedGraph?.find((node) => node.id === parentNodeId);
          if (parentNode?.type === NODE_TYPES.OPTION) {
            toast.warning("Can't go back to an option");
          } else {
            setCurrentNode(selectedNode.parentId);
          }
        }
      }
    },
    [flattenedGraph, setCurrentNode, markNodeCompleted, finishScript],
  );

  const handleCloseCompletionOverlay = useCallback(() => {
    setShowCompletionOverlay(false);
    resetProgress();
    resetCues();
    setCallDuration(0);
    navigate(paths.call);
    close();
    setAutoFoundNodeId(null);
  }, [resetProgress, resetCues, navigate, close]);

  const handleRetry = useCallback(() => {
    window.location.reload();
  }, []);

  const handlePipToggle = useCallback(() => {
    if (PipIsActive) {
      close();
    } else {
      open(pipOptions);
    }
  }, [PipIsActive, open, close, pipOptions]);

  if (audioError && isCallActive) {
    return <ErrorOverlay onRetry={handleRetry} />;
  }

  const content = showCompletionOverlay ? (
    <CallCompletionOverlay
      completedNodeIds={completedNodeIds}
      visitedGroups={visitedGroups}
      flattenedGraph={flattenedGraph ?? []}
      onClose={handleCloseCompletionOverlay}
      orderedGroups={orderedGroups}
      triggered={triggered}
      callDuration={callDuration}
      mode={mode}
    />
  ) : isCallActive ? (
    <div className={styles.page}>
      <main className={styles.content}>
        <CallData
          activeGroupId={mode === "script" ? currentNode?.groupId : undefined}
          groups={mode === "script" ? orderedGroups : []}
          isCallConnected={isCallConnected}
          isPipActive={PipIsActive}
          isPipSupported={isSupported}
          onPipToggle={handlePipToggle}
          callDuration={callDuration}
          setCallDuration={setCallDuration}
        />
        {mode === "listener" ? (
          <ListenerMode
            analyser={analyserNode}
            pendingCues={pendingCues}
            onAcknowledge={onAcknowledge}
          />
        ) : graph ? (
          <>
            <CallProgress
              handleSelectGroup={() => {}}
              activeGroupId={currentNode?.groupId}
              groups={orderedGroups}
            />
            <CallSkip currentNodeId={currentNodeId} onSelectOption={handleChangeOption} />
            <Cues onAcknowledge={onAcknowledge} pendingCues={pendingCues} />
            <div className={styles.script} data-popIn={PipIsActive}>
              <Node
                autoFoundNodeId={autoFoundNodeId}
                completedNodeIds={completedNodeIds}
                currentNodeId={currentNodeId}
                data={structuredGraph ?? []}
                flattenedGraph={flattenedGraph ?? []}
                onProceed={(text) =>
                  onTranscript({
                    text,
                    speaker: "Agent",
                    channel: 0,
                    start: 0,
                    is_interim_result: false,
                    duration: 0,
                  })
                }
                onSelectOption={handleSelectOption}
                orderedGroups={orderedGroups}
              />
            </div>
          </>
        ) : (
          <EmptyState />
        )}
        <button className={styles.endCallButton} onClick={endCall} type="button">
          <img alt="End call" height={24} src={endCallIcon} width={24} />
          End Call
        </button>
      </main>
    </div>
  ) : (
    <NewCall
      onStartCall={startCall}
      isLoading={isLoadingChannels || isLoadingOrganisations || isLoadingGraph}
      handleChannelChange={handleChannelChange}
      channels={channels}
      activeChannelId={activeChannelId}
      organisations={organisations}
      handleOrganisationChange={handleOrganisationChange}
      activeOrganisationId={activeOrganisationId}
      mode={mode}
      onModeChange={setMode}
      noActiveScript={noActiveScript}
      graphLoadFailed={graphLoadFailed}
    />
  );

  if (!isSupported) {
    return content;
  }

  return (
    <>
      {PipIsActive ? <PipActiveOverlay onClose={close} /> : content}
      <PipPortal>{content}</PipPortal>
    </>
  );
};
